import React from 'react';
import { GameProviderId } from '@/common/providers';

interface GameIframeProps {
  gameUrl: string;
  onIframeMessage?: (m: any) => void;
  providerId?: string;
}

interface EGTMessage {
  command?: string;
  gin: string;
  type: string;
}

interface GreenTubeMessage {
  event?: {
    name: string;
  };
}

export enum GameActions {
  CloseGame,
  EgtLoadNewGame,
  EgtReloadCurrentGame,
  Section8ReloadCurrentGame,
  GoToResponsibleGaming,
  GoToCashier,
}

const GameIframe = (props: GameIframeProps) => {
  const egtReceivedMessage = (event: MessageEvent<EGTMessage>) => {
    if (typeof props.onIframeMessage !== 'function') return;
    if (window.config.debugSlotGameIframe) {
      console.log('GameIframe[egtReceivedMessage]', event.data);
    }
    if (!event.data) return;
    if (event.data.command == null) return;

    if (
      'exit' === event.data.command ||
      'com.egt-bg.exit' === event.data.command ||
      'com.egt-bg.launchLobby' === event.data.command
    ) {
      return props.onIframeMessage({ command: GameActions.CloseGame });
    }

    if ('com.egt-bg.launchGame' === event.data.command) {
      try {
        const providerGameId = String(event.data.gin);
        return props.onIframeMessage({ command: GameActions.EgtLoadNewGame, data: providerGameId });
      } catch (e) {}
    }

    /*
    command: "openGame"
    type: "FZWBLSlot"
    */
    if ('openGame' === event.data.command) {
      try {
        const providerGameId = String(event.data.type);
        return props.onIframeMessage({ command: GameActions.EgtLoadNewGame, data: providerGameId });
      } catch (e) {}
    }

    if ('com.egt-bg.reload' === event.data.command) {
      return props.onIframeMessage({ command: GameActions.EgtReloadCurrentGame });
    }
  };

  const egtDigitalReceivedMessage = (event: MessageEvent<EGTMessage>) => {
    if (typeof props.onIframeMessage !== 'function') return;
    if (window.config.debugSlotGameIframe) {
      console.log('GameIframe[egtDigitalReceivedMessage]', event.data);
    }
    if (!event.data) return;
    if (event.data.command == null) return;

    if (
      'exit' === event.data.command ||
      'com.egt-bg.exit' === event.data.command ||
      'com.egt-bg.launchLobby' === event.data.command
    ) {
      return props.onIframeMessage({ command: GameActions.CloseGame });
    }

    if ('com.egt-bg.launchGame' === event.data.command) {
      try {
        const providerGameId = String(event.data.gin);
        return props.onIframeMessage({ command: GameActions.EgtLoadNewGame, data: providerGameId });
      } catch (e) {}
    }

    /*
    command: "openGame"
    type: "FZWBLSlot"
    */
    if ('openGame' === event.data.command) {
      try {
        const providerGameId = String(event.data.type);
        return props.onIframeMessage({ command: GameActions.EgtLoadNewGame, data: providerGameId });
      } catch (e) {}
    }

    if ('com.egt-bg.reload' === event.data.command) {
      return props.onIframeMessage({ command: GameActions.EgtReloadCurrentGame });
    }
  };

  const greentubeReceivedMessage = (event: MessageEvent) => {
    if (typeof props.onIframeMessage !== 'function') return;
    if (window.config.debugSlotGameIframe) {
      console.log('GameIframe[greentubeReceivedMessage]', event.data);
    }
    if (!event.data) return;

    let data: GreenTubeMessage = {};

    try {
      if (typeof event.data === 'string') {
        const tmp = JSON.parse(event.data);
        if (tmp != null) data = tmp;
      }
    } catch (e) {
      return;
    }

    if (data != null && typeof data.event !== 'undefined') {
      if ('gameClosing' === data.event.name) {
        return props.onIframeMessage({ command: GameActions.CloseGame });
      }
    }
  };

  const section8ReceivedMessage = (event: MessageEvent) => {
    if (typeof props.onIframeMessage !== 'function') return;
    if (window.config.debugSlotGameIframe) {
      console.log('GameIframe[section8ReceivedMessage]', event.data);
    }
    if (!event.data) return;
    if (!event.data.method) return;
    const { method } = event.data;
    if ('lobby' === method) {
      return props.onIframeMessage({ command: GameActions.CloseGame });
    }
    if ('responsibleGaming' === method) {
      return props.onIframeMessage({ command: GameActions.GoToResponsibleGaming });
    }
    if ('cashier' === method) {
      return props.onIframeMessage({ command: GameActions.GoToCashier });
    }
    if ('switchToReal' === method || 'reloadGame' === method) {
      return props.onIframeMessage({ command: GameActions.Section8ReloadCurrentGame });
    }
  };

  const genericProviderReceiveMessage = (event: MessageEvent) => {
    if (window.config.debugSlotGameIframe) {
      console.log('GameIframe[genericProviderReceiveMessage]', event.data);
    }
  };

  React.useEffect(() => {
    if (props.providerId === GameProviderId.NOVOMATIC.toString()) {
      window.addEventListener('message', greentubeReceivedMessage, false);
    } else if (props.providerId === GameProviderId.SECTION8.toString()) {
      window.addEventListener('message', section8ReceivedMessage, false);
    } else if (props.providerId === GameProviderId.EGT.toString()) {
      window.addEventListener('message', egtReceivedMessage, false);
    } else if (props.providerId === GameProviderId.EGT_DIGITAL.toString()) {
      window.addEventListener('message', egtDigitalReceivedMessage, false);
    } else {
      window.addEventListener('message', genericProviderReceiveMessage, false);
    }
    return () => {
      if (props.providerId === GameProviderId.NOVOMATIC.toString()) {
        window.removeEventListener('message', greentubeReceivedMessage, false);
      } else if (props.providerId === GameProviderId.SECTION8.toString()) {
        window.removeEventListener('message', section8ReceivedMessage, false);
      } else if (props.providerId === GameProviderId.EGT.toString()) {
        window.removeEventListener('message', egtReceivedMessage, false);
      } else if (props.providerId === GameProviderId.EGT_DIGITAL.toString()) {
        window.removeEventListener('message', egtDigitalReceivedMessage, false);
      } else {
        window.removeEventListener('message', genericProviderReceiveMessage, false);
      }
    };
  }, []);

  return (
    <iframe
      id="game-frame"
      src={props.gameUrl}
      onError={() => {
        console.log('[LAUNCHER] Game error!');
      }}
      width="100%"
      height="100%"
      //autoPlay
      allow={'autoplay *; screen-wake-lock *'}
      title="Game Slot"
    />
  );
};

export default React.memo(GameIframe);
